"use client";

import { useEffect } from "react";

/**
 * Silent SSO probe (multi-domena, ticket-based).
 *
 * Renderowany przez `PublicLayout` gdy: brak sesji frontendu i brak markera
 * `sso_probed` (max 1 probe per okno). Po hydratacji robi
 * top-level redirect na IdP `/ISSO/authorize?prompt=none` - gdy user ma master-sesję
 * na hoście backendu, wraca z `sso_ticket` (callback go redeemuje i ustawia sesję);
 * gdy nie, wraca z `sso=none` (callback ustawia marker, brak pętli).
 *
 * Client-side (nie SSR/middleware): boty bez JS widzą normalny SSR (SEO bez regresji);
 * redirect dotyka tylko realnych przeglądarek anonimowych użytkowników.
 *
 * `state` (CSRF) zapisywany jako krótkotrwałe cookie double-submit i echo'owany
 * przez IdP z powrotem do callbacku, gdzie jest weryfikowany.
 */
export function SsoProbe({ apiBase }: { apiBase: string }) {
  useEffect(() => {
    if (!apiBase || typeof window === "undefined") return;

    try {
      const bytes = new Uint8Array(16);
      window.crypto.getRandomValues(bytes);
      const state = Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

      const secure = window.location.protocol === "https:" ? "; Secure" : "";
      document.cookie = `sso_state=${state}; Path=/; Max-Age=300; SameSite=Lax${secure}`;

      const returnTo = window.location.pathname + window.location.search;
      const callback =
        window.location.origin + "/api/sso/callback?return_to=" + encodeURIComponent(returnTo);

      const authorize =
        apiBase.replace(/\/$/, "") +
        "/ISSO/authorize?prompt=none&return=" +
        encodeURIComponent(callback) +
        "&state=" +
        encodeURIComponent(state);

      window.location.replace(authorize);
    } catch {
      /* fail-soft: bez probe (SSO po prostu nie zadziała ambient dla tej wizyty). */
    }
  }, [apiBase]);

  return null;
}
